import { useCallback, useEffect, useState } from 'react';
import { ConfirmDialog } from './components/ConfirmDialog';
import { Palette } from './components/Palette';
import { Shell, type Route } from './components/Shell';
import { session } from './lib/session';
import { Calendar } from './pages/Calendar';
import { Chat } from './pages/Chat';
import { Notes } from './pages/Notes';
import { TalkMode } from './components/TalkMode';
import { Games } from './pages/Games';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Permissions } from './pages/Permissions';
import { Routines } from './pages/Routines';
import { Server } from './pages/Server';
import { Settings } from './pages/Settings';
import { Stats } from './pages/Stats';
import { Widget } from './pages/Widget';
import { Caller } from './pages/Caller';
import { AvatarEmbed } from './pages/AvatarEmbed';
import type { MicSignal } from './components/Composer';
import { Integrations } from './pages/Integrations';
import { ElenaProvider, useElena } from './state';
import { useWake } from './lib/useWake';

const routes: Route[] = [
  'home',
  'chat',
  'routines',
  'games',
  'notes',
  'calendar',
  'server',
  'stats',
  'integrations',
  'widget',
  'permissions',
  'settings',
];

const readRoute = (): Route => {
  const r = location.hash.replace(/^#\/?/, '').split('?')[0] as Route;
  return routes.includes(r) ? r : 'home';
};

export function App() {
  const [authed, setAuthed] = useState(Boolean(session.token()));
  const [route, setRoute] = useState<Route>(readRoute);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [micSignal, setMicSignal] = useState<MicSignal>(null);

  useEffect(() => {
    const onHash = () => {
      setRoute(readRoute());
      document.getElementById('main')?.focus({ preventScroll: true });
      window.scrollTo({ top: 0 });
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onUnauthorized = useCallback(() => setAuthed(false), []);

  // seed: words already heard after "Hej Elena" (a click passes an event, not a string);
  // fromWake: those words are also in the mic tap's memory, for the real recognizer
  const onMic = useCallback((seed?: unknown, fromWake?: boolean) => {
    const r = readRoute();
    if (r !== 'home' && r !== 'chat' && r !== 'widget' && r !== 'games') location.hash = '#/home';
    const words = typeof seed === 'string' ? seed : '';
    setMicSignal((s) => ({ n: (s?.n ?? 0) + 1, seed: words, wake: fromWake === true && !!words }));
  }, []);

  // The phone app's 3D Elena: no token, no app around it
  if (location.hash.startsWith('#/avatar')) return <AvatarEmbed />;

  if (!authed) return <Login onDone={() => setAuthed(true)} />;

  // Elena's phone: its own connection (as "caller"), none of the app around it
  if (location.hash.startsWith('#/caller')) return <Caller />;

  return (
    <ElenaProvider onUnauthorized={onUnauthorized}>
      {route === 'widget' ? (
        <Widget micSignal={micSignal} onMic={onMic} />
      ) : (
        <Main
          route={route}
          micSignal={micSignal}
          onMic={onMic}
          onPalette={() => setPaletteOpen(true)}
        />
      )}
      <Palette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <TalkMode />
      <ConfirmDialog />
    </ElenaProvider>
  );
}

function Main({
  route,
  micSignal,
  onMic,
  onPalette,
}: {
  route: Route;
  micSignal: MicSignal;
  onMic: (seed?: unknown, fromWake?: boolean) => void;
  onPalette: () => void;
}) {
  const wake = useWake(onMic);
  const { game } = useElena();
  // A game started by voice from anywhere opens its board.
  useEffect(() => {
    if (game && route !== 'games' && route !== 'widget') location.hash = '#/games';
  }, [game?.game]);
  return (
    <Shell route={route} onPalette={onPalette} onMic={onMic} wake={wake}>
      {route === 'home' && <Home micSignal={micSignal} />}
      {route === 'chat' && <Chat micSignal={micSignal} />}
      {route === 'server' && <Server />}
      {route === 'stats' && <Stats />}
      {route === 'permissions' && <Permissions />}
      {route === 'settings' && <Settings wake={wake} />}
      {route === 'routines' && <Routines />}
      {route === 'games' && <Games micSignal={micSignal} />}
      {route === 'notes' && <Notes />}
      {route === 'calendar' && <Calendar />}
      {route === 'integrations' && <Integrations />}
    </Shell>
  );
}
